$background-gradient: linear-gradient(0deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 0.3) 100%);
$background-gradient-hover: linear-gradient(
  0deg,
  rgba(0, 0, 0, 0.2) 0%,
  rgba(0, 0, 0, 0.3) 50%,
  rgba(0, 0, 0, 0.5) 100%
);

:host {
  display: block;
  @include eon-typography();
}

:host([use-as-background=""]),
:host([use-as-background="true"]) {
  @include position-absolute();
  width: 100%;
  height: 100%;
  z-index: auto;

  .#{$prefix}videoplayer,
  .#{$prefix}relative {
    width: 100% !important;
    height: 100% !important;
  }
}

.#{$prefix}videoplayer {
  margin: 0;
  padding: 0;
  width: 100%;
  cursor: pointer;

  &:not(.#{$prefix}force-full-height) {
    @if variable-exists(videoplayer-default-shadow) {
      box-shadow: $videoplayer-default-shadow;
    }
  }

  &.#{$prefix}click-event {
    pointer-events: none;
  }

  &:focus {
    outline: 4px solid $videoplayer-focus-outline-color;
  }

  // don't combine this with other selectors as it won't work in legacy Edge which will then skip the entire block
  &:focus-within {
    outline: 4px solid $videoplayer-focus-outline-color;
    eon-ui-button {
      --primaryBackgroundColor: #{v(playButtonColor)} !important;
    }
  }

  &:focus,
  &:hover {
    eon-ui-button {
      --primaryBackgroundColor: #{v(playButtonColor)} !important;
    }
  }

  &.#{$prefix}force-full-height {
    height: 100%;
  }

  /* Without this, Safari on MacOS will just show a black screen in Fullscreen mode. */
  /* stylelint-disable selector-no-vendor-prefix */
  &:-webkit-full-screen {
    height: 100%;
    max-width: 100%;
    width: 100%;
  }

  .#{$prefix}relative {
    height: 0;
    margin: 0;
    max-height: 100%;
    overflow: hidden;
    padding: 0;
    padding-top: 56.25%; /* 16:9 Aspect Ratio */
    position: relative;
    width: 100%;

    &.#{$prefix}fullscreen {
      height: 100%;
      padding: 0;
    }

    &.#{$prefix}force-full-height {
      height: 100%;
      padding: 0;
    }

    .#{$prefix}absolute {
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;

      .#{$prefix}player {
        display: block;
        height: 100%;
        left: 50%;
        min-width: 100%;
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);

        &.#{$prefix}iOSYoutubeHack {
          z-index: 95;
          visibility: visible;

          &.#{$prefix}placeholder {
            opacity: 0;
          }

          &.#{$prefix}playing {
            z-index: auto;
            opacity: 1;
          }
        }

        &.#{$prefix}fullscreen {
          left: 0;
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 100%;
          video {
            width: 50%;
          }
        }

        &.#{$prefix}fullscreen.#{$prefix}safari {
          video {
            transform: unset !important;
          }
        }

        &.#{$prefix}click-event {
          pointer-events: none;
        }
      }

      .#{$prefix}video-wrapper {
        outline: none;
        &.#{$prefix}start {
          display: block;
          height: 100%;
          left: 50%;
          min-width: 100%;
          position: absolute;
          top: 50%;
          transform: translate(-50%, -50%);
          overflow: hidden;
        }
        &.#{$prefix}start::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 200%;
          background: $background-gradient;
          transition: ease-in 0.2s;
        }
        &.#{$prefix}playing::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 200%;
          transition: ease-in 0.2s;
        }
        &.#{$prefix}playing:hover::before {
          background: $background-gradient-hover;
        }
        &.#{$prefix}start:hover::before {
          background: $background-gradient-hover;
          transform: translateY(-10%);
        }
        &.#{$prefix}start:focus {
          outline: none;
        }
        &.#{$prefix}pause {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 200%;
          overflow: hidden;
        }

        &.#{$prefix}pause,
        &.#{$prefix}pause-youtube {
          &.#{$prefix}background-video::before {
            background: none;
          }
        }

        &.#{$prefix}pause::before,
        &.#{$prefix}pause-youtube::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 200%;
          background: $background-gradient-hover;
          overflow: hidden;
          animation: fadeIn ease 0.5s;
        }

        &.#{$prefix}pause-youtube::before {
          pointer-events: none;
        }

        @keyframes fadeIn {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }

        &.#{$prefix}click-event {
          pointer-events: none;
        }

        &.#{$prefix}shadow-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 200%;
          overflow: hidden;
          background: $background-gradient-hover;
        }
      }
    }
  }

  .#{$prefix}placeholder {
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;

    .#{$prefix}adaptiveImage {
      display: block;
      height: 100%;
      left: 50%;
      min-height: 100%;
      min-width: 100%;
      object-fit: cover; // unstretches 2x1 images in the 16:9 container
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
    }

    &.#{$prefix}click-event {
      pointer-events: none;
    }
  }

  .#{$prefix}video-container {
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
    pointer-events: none;
    z-index: 1;

    .#{$prefix}content {
      &-wrapper {
        background: transparent;
      }

      &-headline {
        position: absolute;
        left: 40px;
        top: 40px;
        margin: 0;
        padding: 0;
        text-align: left;
        &.#{$prefix}invisible-in-small-grids {
          opacity: 0;
        }
      }

      &-button {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        margin: 0;
        padding: 0;
        .#{$prefix}invisible-in-small-grids {
          opacity: 0;
        }
        .#{$prefix}video-replay {
          --primaryBackgroundColor: #{v(playButtonColor)} !important;
          opacity: 1;
        }

        @if variable-exists(videoplayer-button-radius) {
          --buttonRadius: #{$videoplayer-button-radius} !important;
        }
      }

      .#{$prefix}iconButton {
        &.#{$prefix}centered {
          text-align: center;
        }
      }

      .#{$prefix}headline + .#{$prefix}iconButton {
        margin-top: 32px;

        @include for-size(tablet-or-smaller) {
          margin-top: 24px;
        }
      }
    }
  }

  .#{$prefix}error {
    align-items: center;
    color: $videoplayer-error-color;
    display: flex;
    height: 100%;
    left: 0;
    position: absolute;
    text-align: center;
    top: 0;
    width: 100%;
    z-index: 1;

    &::after {
      // background layer
      background: $videoplayer-error-background-color;
      content: "";
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
      z-index: -1;
    }

    &.#{$prefix}semitransparent::after {
      opacity: 0.75;
    }

    &-content {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      margin: auto;
      z-index: 2;

      .#{$prefix}error-text {
        margin: 0;
        text-align: center;
      }

      .#{$prefix}error-icon {
        height: 32px;
        margin: 15px;
        width: 32px;
      }
    }
  }

  &.#{$prefix}is-touch {
    .#{$prefix}video-wrapper.#{$prefix}playing:hover::before {
      background: none !important;
    }
  }
}
